/**
 * Applies SEO tags to the live document during client-side navigation.
 *
 * The static HTML produced by scripts/generate-seo-pages.js already carries the right tags on first
 * paint. This module keeps them correct after the router takes over, since a SPA navigation does not
 * reload the document and would otherwise leave the previous page's title and canonical in place.
 *
 * Every write is an upsert against the tag the build step emitted, so nothing is duplicated and
 * there is no cleanup to sequence between route changes.
 */

const JSON_LD_ID = 'seo-jsonld';

function upsertMeta(attrName, attrValue, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(data) {
  const existing = document.getElementById(JSON_LD_ID);
  if (!data) {
    if (existing) existing.remove();
    return;
  }
  const el = existing || document.createElement('script');
  if (!existing) {
    el.type = 'application/ld+json';
    el.id = JSON_LD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * @param {object} seo
 * @param {string} seo.title
 * @param {string} seo.description
 * @param {string|null} [seo.canonical] Absolute URL, or null to drop the tag
 * @param {string} [seo.robots]
 * @param {string} [seo.ogType]
 * @param {string} [seo.image] Absolute URL
 * @param {object|null} [seo.jsonLd]
 */
export function applySeo({
  title,
  description,
  canonical,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  ogType = 'website',
  image,
  jsonLd,
}) {
  if (title) document.title = title;

  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'description', description);
  upsertMeta('property', 'og:type', ogType);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);

  if (image) {
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:image', image);
  }

  upsertCanonical(canonical);
  upsertMeta('property', 'og:url', canonical || undefined);
  setJsonLd(jsonLd);
}

export default applySeo;
